export const DROPDOWN_MAX_HEIGHT = 300;

const EDGE_MARGIN = 8;

export interface VerticalBounds {
  top: number;
  bottom: number;
}

/**
 * Where the list goes, relative to a clipping box laid exactly over `bounds`. Exactly one of `top`
 * and `bottom` is set; `bottom` means the list opened above its trigger.
 */
export interface MenuPosition {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
  bounds: VerticalBounds;
}

/**
 * Where a list goes relative to its trigger. Every input is in the pixels getBoundingClientRect
 * reports, and nothing is measured against `100vh`: on an iPhone in Safari that is the height with
 * the toolbars hidden, about 40px more than is visible, so a list anchored by it floated clear of
 * its field (#35350).
 *
 * `bounds` is the visible part of whatever scrolls the field. The list is drawn inside a box over
 * it that clips, so a list that follows its field while the form scrolls passes under the header
 * the way an inline Northstar dropdown does, instead of riding over it.
 *
 * Height is decided when the list opens. While the form scrolls, pass the open list's position as
 * `current` and its rendered height as `height`: the list keeps its size and flips between below
 * and above as the room changes, preferring below, as the Northstar dropdowns on the same form do.
 */
export const getMenuPosition = (
  rect: Pick<DOMRect, 'top' | 'bottom' | 'left' | 'width'>,
  bounds: VerticalBounds,
  current?: MenuPosition,
  height?: number
): MenuPosition => {
  const spaceBelow = bounds.bottom - rect.bottom;
  const spaceAbove = rect.top - bounds.top;
  const needed = (current ? (height ?? current.maxHeight) : DROPDOWN_MAX_HEIGHT);
  const showAbove = spaceBelow < needed && spaceAbove > spaceBelow;
  const room = (showAbove ? spaceAbove : spaceBelow) - EDGE_MARGIN;
  const maxHeight = current ? current.maxHeight : Math.max(0, Math.min(DROPDOWN_MAX_HEIGHT, room));
  const common = { left: rect.left, width: rect.width, maxHeight, bounds };

  return showAbove
    ? { ...common, bottom: bounds.bottom - rect.top }
    : { ...common, top: rect.bottom - bounds.top };
};

/**
 * The visible extent of the nearest ancestor that scrolls, clipped to the viewport. Falls back to
 * the viewport when nothing scrolls.
 */
export const getScrollBounds = (element: HTMLElement, viewportHeight: number): VerticalBounds => {
  for (let el = element.parentElement; el; el = el.parentElement) {
    const overflowY = window.getComputedStyle(el).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      const rect = el.getBoundingClientRect();
      return { top: Math.max(0, rect.top), bottom: Math.min(viewportHeight, rect.bottom) };
    }
  }
  return { top: 0, bottom: viewportHeight };
};
